import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import hookTrackService, {
  HookTrack,
  HookTrackMetrics,
  HookTrackMetricsItem,
  CreateHookTrackPayload,
  UpdateHookTrackTitlePayload,
  UpdateHookTrackTrafficPayload,
} from '@/services/hookTrackService';

interface HookTrackStore {
  // State
  tests: HookTrack[];
  currentTest: HookTrack | null;
  metrics: HookTrackMetricsItem[];
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchUserTests: (userId: string) => Promise<void>;
  fetchTestById: (testId: string) => Promise<void>;
  fetchTestMetrics: (testId: string, inicio?: string, fim?: string) => Promise<void>;
  createTest: (
    originalVideoId: string,
    data: CreateHookTrackPayload
  ) => Promise<HookTrack | null>;
  updateTestTitle: (
    testId: string,
    data: UpdateHookTrackTitlePayload
  ) => Promise<void>;
  updateTestTraffic: (
    testId: string,
    data: UpdateHookTrackTrafficPayload
  ) => Promise<void>;
  deleteTest: (testId: string) => Promise<void>;
  selectTestVideo: (testId: string) => Promise<any>;
  getTestByVideoId: (videoId: string) => Promise<HookTrack | null>;
  setCurrentTest: (test: HookTrack | null) => void;
  resetError: () => void;
  reset: () => void;
}

const initialState = {
  tests: [],
  currentTest: null,
  metrics: [],
  isLoading: false,
  error: null,
};

export const useHookTrackStore = create<HookTrackStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      fetchUserTests: async (userId: string) => {
        set({ isLoading: true, error: null });
        try {
          const tests = await hookTrackService.getUserHookTracks(userId);
          set({ tests, isLoading: false });
        } catch (error: any) {
          console.error('Erro ao buscar testes de hook:', error);
          set({
            error: error?.message || 'Erro ao buscar testes de hook',
            isLoading: false,
          });
        }
      },

      fetchTestById: async (testId: string) => {
        set({ isLoading: true, error: null });
        try {
          // Busca o teste da lista local
          const currentTest = get().tests.find(
            (t) => t.id === testId || t.id_lead_test === testId
          );
          if (currentTest) {
            set({
              currentTest,
              isLoading: false,
            });
          } else {
            set({ isLoading: false });
          }
        } catch (error: any) {
          console.error('Erro ao buscar teste de hook:', error);
          set({
            error: error?.message || 'Erro ao buscar teste de hook',
            isLoading: false,
          });
        }
      },

      fetchTestMetrics: async (testId: string, inicio?: string, fim?: string) => {
        set({ isLoading: true, error: null });
        try {
          const metrics = await hookTrackService.getHookTrackMetrics(
            testId,
            inicio,
            fim
          );
          set({ metrics, isLoading: false });
        } catch (error: any) {
          console.error('Erro ao buscar métricas do teste:', error);
          set({
            error: error?.message || 'Erro ao buscar métricas do teste',
            isLoading: false,
          });
        }
      },

      createTest: async (originalVideoId: string, data: CreateHookTrackPayload) => {
        set({ isLoading: true, error: null });
        try {
          const newTest = await hookTrackService.createHookTrack(
            originalVideoId,
            data
          );
          set((state) => ({
            tests: [...state.tests, newTest],
            currentTest: newTest,
            isLoading: false,
          }));
          return newTest;
        } catch (error: any) {
          console.error('Erro ao criar teste de hook:', error);
          const message =
            error?.response?.data?.message ||
            error?.message ||
            'Erro ao criar teste de hook';
          set({
            error: message,
            isLoading: false,
          });
          throw new Error(message);
        }
      },

      updateTestTitle: async (testId: string, data: UpdateHookTrackTitlePayload) => {
        set({ isLoading: true, error: null });
        try {
          await hookTrackService.updateHookTrackTitle(testId, data);
          // Atualiza o teste localmente
          set((state) => ({
            tests: state.tests.map((t) =>
              t.id === testId || t.id_lead_test === testId
                ? { ...t, name: data.leadTestTitle, leadTestTitle: data.leadTestTitle, title: data.leadTestTitle }
                : t
            ),
            currentTest:
              state.currentTest?.id === testId ||
              state.currentTest?.id_lead_test === testId
                ? {
                    ...state.currentTest,
                    name: data.leadTestTitle,
                    leadTestTitle: data.leadTestTitle,
                    title: data.leadTestTitle,
                  }
                : state.currentTest,
            isLoading: false,
          }));
        } catch (error: any) {
          console.error('Erro ao atualizar título do teste:', error);
          set({
            error: error?.message || 'Erro ao atualizar título do teste',
            isLoading: false,
          });
        }
      },

      updateTestTraffic: async (
        testId: string,
        data: UpdateHookTrackTrafficPayload
      ) => {
        set({ isLoading: true, error: null });
        try {
          const response = await hookTrackService.updateHookTrackTraffic(
            testId,
            data
          );
          // Atualiza os vídeos do teste com os novos percentuais
          set((state) => ({
            tests: state.tests.map((t) =>
              t.id === testId || t.id_lead_test === testId
                ? {
                    ...t,
                    videos: t.videos?.map((v) => {
                      const updatedVideo = data.videosIds.find(
                        (vid) => vid.id_video === v.id || vid.id_video === v.id_video
                      );
                      return updatedVideo
                        ? { ...v, trafego: updatedVideo.trafego, percentage: updatedVideo.trafego }
                        : v;
                    }),
                  }
                : t
            ),
            isLoading: false,
          }));
        } catch (error: any) {
          console.error('Erro ao atualizar tráfego do teste:', error);
          set({
            error: error?.message || 'Erro ao atualizar tráfego do teste',
            isLoading: false,
          });
        }
      },

      deleteTest: async (testId: string) => {
        set({ isLoading: true, error: null });
        try {
          await hookTrackService.deleteHookTrack(testId);
          set((state) => ({
            tests: state.tests.filter(
              (t) => t.id !== testId && t.id_lead_test !== testId
            ),
            currentTest:
              state.currentTest?.id === testId ||
              state.currentTest?.id_lead_test === testId
                ? null
                : state.currentTest,
            isLoading: false,
          }));
        } catch (error: any) {
          console.error('Erro ao deletar teste de hook:', error);
          set({
            error: error?.message || 'Erro ao deletar teste de hook',
            isLoading: false,
          });
        }
      },

      selectTestVideo: async (testId: string) => {
        try {
          const video = await hookTrackService.selectHookTrackVideo(testId);
          return video;
        } catch (error: any) {
          console.error('Erro ao selecionar vídeo do teste:', error);
          set({
            error: error?.message || 'Erro ao selecionar vídeo do teste',
          });
          return null;
        }
      },

      getTestByVideoId: async (videoId: string) => {
        try {
          const test = await hookTrackService.getHookTrackByVideoId(videoId);
          return test;
        } catch (error: any) {
          console.error('Erro ao buscar teste por ID do vídeo:', error);
          set({
            error: error?.message || 'Erro ao buscar teste por ID do vídeo',
          });
          return null;
        }
      },

      setCurrentTest: (test) => {
        set({ currentTest: test });
      },

      resetError: () => {
        set({ error: null });
      },

      reset: () => {
        set(initialState);
      },
    }),
    { name: 'HookTrackStore' }
  )
);
